import {
  faBookBookmark,
  faClipboardList,
  faCloudBolt,
  faCommentsQuestionCheck,
  faIdCard,
  faMessages,
} from '@fortawesome/duotone-regular-svg-icons';
import { faCalendarExclamation } from '@fortawesome/pro-duotone-svg-icons';
import { faListCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RoleProtected } from '~/common/components/RoleProtected';
import { UNDERWRITER_ROLES, USER_ROLE } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import {
  type RailNavClickEvent,
  useDynaTracking,
} from '~/common/hooks/useDynaTracking';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import useUrl from '~/common/hooks/useUrl';
import { useModalsStore } from '~/modals/modalStore';
import { classifyKeys } from '~/pages/ClassifyDocumentsV2/hooks/queries';
import { HomeActions, HomeThunks } from '~/pages/Home/redux';

import navCategories from '../../SummaryWrapper/navCategories';
import { EXTERNAL_LINKS, buildSubmitIssueUrl } from '../constants';
import useRailRouteMatches from '../hooks/useRailRouteMatches';
import {
  RailModalButton,
  RailNavExternalLink,
  RailNavLink,
} from './RailNavButton';
import {
  RailExternalGroup,
  RailModalDivider,
  RailModalGroup,
  RailNavigationGroup,
} from './RailNavGroup';

export default function RailNav() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { sendRailNavClickEvent } = useDynaTracking();

  const { isBOLTRouteMatch, is1003Match, isKnowYourLoanMatch } =
    useRailRouteMatches();

  const {
    obfuscatedLoanIdentifier: {
      obfuscatedLenderDatabaseId,
      obfuscatedLoanRecordId,
    },
  } = useUrl();
  const { isUnderwriter } = useUser();
  const { loanNumber, loanStatus } = useGetLoanSummary().data?.loanInfo || {};

  const openModal = useModalsStore((s) => s.openModal);
  const currentModal = useModalsStore((s) => s.currentModal);

  const baseRoute = `/${obfuscatedLenderDatabaseId}/${obfuscatedLoanRecordId}`;

  const featureFlags = useNavFeatureFlags();

  const default1003Route =
    navCategories.find(({ flagName }) => featureFlags[flagName])?.to || '';

  const { loanSummaryUpsertOrDelete } = useSelector(
    ({ loanSummaryUpsertOrDelete }) => loanSummaryUpsertOrDelete,
  );

  const selectedCategory = useSelector((s) => s.home.selectedCategory);

  const handleOutsideBoltUpdates = useCallback(() => {
    if (loanSummaryUpsertOrDelete) {
      dispatch(HomeActions.setSelectedAlert(null));
      queryClient.invalidateQueries({
        queryKey: [classifyKeys.documentTypeAssociations],
      });
      dispatch(HomeThunks.getNavigationDetails(false, isUnderwriter));
    }
  }, [dispatch, loanSummaryUpsertOrDelete, queryClient, isUnderwriter]);

  const handleBoltClick = useCallback(() => {
    handleOutsideBoltUpdates();
    // Fallback: If no selectedCategory exists, default to 0 (Classify)
    if (selectedCategory === undefined || selectedCategory === null) {
      dispatch(HomeActions.setSelectedCategory(0));
    }
  }, [handleOutsideBoltUpdates, selectedCategory, dispatch]);

  return (
    <>
      {/* Navigation */}
      <RailNavigationGroup aria-label="internal navigation">
        <RailNavLink
          to={baseRoute}
          onClick={() => {
            handleBoltClick();
            sendRailNavClickEvent({ buttonName: 'BOLT' });
          }}
          isActive={isBOLTRouteMatch}
          buttonTitle="BOLT"
          icon={
            <img
              alt="Bolt Logo"
              src={
                isBOLTRouteMatch
                  ? '/images/bolt-logo.svg'
                  : '/images/bolt-logo-recolored.svg'
              }
            />
          }
          navType="internal"
        />
        <RailNavLink
          isActive={is1003Match}
          label="1003 Information"
          to={`${baseRoute}/summary/${default1003Route}`}
          onClick={() => {
            dispatch(HomeActions.setSelectedCategory(null));
            sendRailNavClickEvent({ buttonName: '1003 Information' });
          }}
          icon={<FontAwesomeIcon icon={faClipboardList} />}
          navType="internal"
        />
        <RoleProtected roles={UNDERWRITER_ROLES}>
          <RailNavLink
            isActive={isKnowYourLoanMatch}
            to={`${baseRoute}/know-your-loan/primary`}
            onClick={() => sendRailNavClickEvent({ buttonName: 'KYL' })}
            label="KYL"
            icon={<FontAwesomeIcon icon={faBookBookmark} />}
            navType="internal"
          />
        </RoleProtected>
      </RailNavigationGroup>
      <RailModalDivider width="48px" />
      {/* Modals */}
      <RailModalGroup aria-label="modal navigation">
        <RoleProtected roles={UNDERWRITER_ROLES}>
          <RailModalButton
            anchorId="dateTracking"
            onClick={() => {
              openModal('dateTracking');
              sendRailNavClickEvent({ buttonName: 'Date Tracking' });
            }}
            navType="modal"
            label="Date Tracking"
            icon={<FontAwesomeIcon icon={faCalendarExclamation} />}
            isActive={currentModal === 'dateTracking'}
          />
          <RailModalDivider />
          <RailModalButton
            anchorId="loanComments"
            onClick={() => {
              openModal('loanComments');
              sendRailNavClickEvent({ buttonName: 'Comments' });
            }}
            navType="modal"
            label="Comments"
            icon={<FontAwesomeIcon icon={faMessages} />}
            isActive={currentModal === 'loanComments'}
          />
          <RailModalDivider />
          <RailModalButton
            anchorId="loanContacts"
            onClick={() => {
              openModal('loanContacts');
              sendRailNavClickEvent({ buttonName: 'Contacts' });
            }}
            navType="modal"
            label="Contacts"
            icon={<FontAwesomeIcon icon={faIdCard} />}
            isActive={currentModal === 'loanContacts'}
          />
          <RailModalDivider />
        </RoleProtected>
        <RailModalButton
          anchorId="loanSummaryDialog"
          label="Loan Summary"
          onClick={() => {
            openModal('loanSummaryDialog');
            sendRailNavClickEvent({ buttonName: 'Loan Summary' });
          }}
          navType="modal"
          icon={
            <FontAwesomeIcon
              icon={faListCheck}
              color={theme.palette?.blue[500]}
            />
          }
          isActive={currentModal === 'loanSummaryDialog'}
        />
      </RailModalGroup>
      {/* External */}
      <RailExternalGroup aria-label="external navigation">
        <ChatUwmButton sendRailNavClickEvent={sendRailNavClickEvent} />
        <RoleProtected roles={[USER_ROLE.UNDERWRITER]}>
          <RailNavExternalLink
            onClick={() => {
              openModal('boltIncidentInterceptor');
              sendRailNavClickEvent({ buttonName: 'BOLT Incident' });
            }}
            label="BOLT Incident"
            icon={<FontAwesomeIcon icon={faCloudBolt} />}
            navType="external"
          />
        </RoleProtected>
        <RoleProtected notInRoles={UNDERWRITER_ROLES}>
          <RailNavExternalLink
            href={buildSubmitIssueUrl({
              loanNumber,
              loanStatus,
              obfuscatedLoanRecordId,
            })}
            onClick={() =>
              sendRailNavClickEvent({ buttonName: 'Submit Issue' })
            }
            label="Submit Issue"
            icon={<FontAwesomeIcon icon={faCloudBolt} />}
            navType="external"
          />
        </RoleProtected>
        <RoleProtected notInRoles={UNDERWRITER_ROLES}>
          <RailNavExternalLink
            href={EXTERNAL_LINKS.SUGGEST_IDEAS}
            onClick={() =>
              sendRailNavClickEvent({ buttonName: 'Suggest Ideas' })
            }
            label="Suggest Ideas"
            icon={<FontAwesomeIcon icon={faCommentsQuestionCheck} />}
            navType="external"
          />
        </RoleProtected>
      </RailExternalGroup>
    </>
  );
}

type ChatUwmButtonProps = {
  sendRailNavClickEvent: (event: RailNavClickEvent) => void;
};

function ChatUwmButton({
  sendRailNavClickEvent,
}: Readonly<ChatUwmButtonProps>) {
  return (
    <RailNavExternalLink
      href={EXTERNAL_LINKS.CHAT_UWM}
      onClick={() => sendRailNavClickEvent({ buttonName: 'ChatUWM' })}
      label="ChatUWM"
      icon={<img src="/images/chatUWM.svg" alt="ChatUWM Logo" />}
    />
  );
}
